import { AccordionListItemBuilder, AppFunctionComponent } from "../../types"
import { Options, RenderNode } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES, MARKS, Node } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import React, { ReactNode } from "react"
import {
  PrimaryHeading,
  QuaternaryHeading,
  QuinaryHeading,
  SecondaryHeading,
  SenaryHeading,
  TertiaryHeading,
} from "../../components/common/heading.component"
import {
  getEmbedFields,
  isAnchor,
  isAudio,
  isButton,
  isDownloadButton,
  mapEmbeddedEntryToAnchor,
  mapEmbeddedEntryToAudio,
  mapEmbeddedEntryToButtonLink,
  mapEmbeddedEntryToDownloadButton,
  resolveIsEmail,
} from "../../contentful/options/rich-text"
import { RenderRichText } from "../../types"
import {
  BasicButtonLink,
  RawLink,
} from "../../ui/components/link/link.component"
import { resolveLink } from "../../ui/components/link/link.helpers"
import { AudioComponentResolver } from "../audio/audio-component.resolver"
import { DownloadButtonResolver } from "../download-button/download-button.resolver"
import { getImage } from "gatsby-plugin-image"
import { Image } from "../../components/common/image.component"
import styled from "styled-components"
import ResponsiveImageResolver from "../responsive-image/responsive-image.resolver"
import AccordionItem from "../../ui/components/accordion/accordion-item.component"
import { interactiveAccordion } from "../../ui/helpers/interactive-accordion/interactive-accordion-item.hook"
import { kebabCase } from "lodash"
import { Button } from "../../ui/components/button/button.styled"
interface Props {
  content: RenderRichText
  options?: RenderNode
  children?: React.ReactNode
}

const AssetImage = styled(Image)`
  img {
    margin: auto;
  }
`

const StyledText = styled.div`
  .coupon-code {
    display: flex;
    font-weight: 800;
    letter-spacing: 1.5px;
    font-size: 1.2em;
    width: min-content;
    padding: 15px 40px;
    justify-content: center;
    align-items: center;
    background: #eee;
    border: 2px dashed black;
    cursor: pointer;
  }
`

const getRenderMark = () => {
  const copyToClipboard = () => {
    const couponElement = document.querySelector(".coupon-code")
    if (couponElement) {
      const couponCode = couponElement.textContent
      navigator.clipboard
        .writeText(couponCode!)
        .then(() => {
          alert("Copied")
        })
        .catch(() => {
          console.error("Failed to copy")
        })
    }
    const inlineCodeElement = document.querySelector(".inline-code")
    if (inlineCodeElement) {
      const inlineCode = inlineCodeElement.textContent
      navigator.clipboard.writeText(inlineCode!).then(() => {
        alert("Copied")
      })
    }
  }
  return {
    [MARKS.BOLD]: (text: ReactNode) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text: ReactNode) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text: ReactNode) => <u>{text}</u>,
    [MARKS.CODE]: (text: ReactNode) =>
      text ? (
        <StyledText
          style={{
            display: "inline-block",
            verticalAlign: text.toString().includes("<s>") ? "unset" : "middle",
            lineHeight: "normal",
          }}
          onClick={copyToClipboard}
          dangerouslySetInnerHTML={{ __html: text.toString() }}
        />
      ) : (
        ""
      ),
  }
}

const getHeadings = () => {
  return {
    [BLOCKS.HEADING_1]: (_: any, children: ReactNode) => (
      <PrimaryHeading renderAs={"h1"}>{children}</PrimaryHeading>
    ),
    [BLOCKS.HEADING_2]: (_: any, children: ReactNode) => (
      <SecondaryHeading renderAs={"h2"}>{children}</SecondaryHeading>
    ),
    [BLOCKS.HEADING_3]: (_: any, children: ReactNode) => (
      <TertiaryHeading renderAs={"h3"}>{children}</TertiaryHeading>
    ),
    [BLOCKS.HEADING_4]: (_: any, children: ReactNode) => (
      <QuaternaryHeading renderAs={"h4"}>{children}</QuaternaryHeading>
    ),
    [BLOCKS.HEADING_5]: (_: any, children: ReactNode) => (
      <QuinaryHeading renderAs={"h5"}>{children}</QuinaryHeading>
    ),
    [BLOCKS.HEADING_6]: (_: any, children: ReactNode) => (
      <SenaryHeading renderAs={"h6"}>{children}</SenaryHeading>
    ),
  }
}

const getHyperlinks = () => {
  return {
    [INLINES.ASSET_HYPERLINK]: (node: Node, children: ReactNode) => {
      const fields = getEmbedFields(node)
      return (
        <RawLink to={resolveLink(fields.file.url)} download>
          {children}
        </RawLink>
      )
    },
    [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => {
      return <RawLink to={resolveIsEmail(node.data.uri)}>{children}</RawLink>
    },
  }
}

const getEmbedded = () => {
  return {
    // [REFERENCES.ACCORDION_LIST_ITEM]: (node: Node) => {
    //   return <AccordionList {...(node as any)} />
    // },
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
      if (node.data.target?.internal?.type === "ContentfulResponsiveImage") {
        return <ResponsiveImageResolver {...node.data.target} />
      }

      if (isButton(node)) {
        return mapEmbeddedEntryToButtonLink(node)
      }
      if (isDownloadButton(node)) {
        const { ...props } = mapEmbeddedEntryToDownloadButton(node)
        return <DownloadButtonResolver {...props} />
      }
      if (isAudio(node)) {
        const { ...props } = mapEmbeddedEntryToAudio(node)
        return <AudioComponentResolver {...props} />
      }
      if (isAnchor(node)) {
        const { anchorId } = mapEmbeddedEntryToAnchor(node)
        return <div id={anchorId} />
      }
      /* if needed then add other types to map */
      return null
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
      /* TODO: wonder if better way exists */
      const fields = getEmbedFields(node)
      const image = getImage(fields)

      if (image && fields) {
        return <AssetImage image={image} alt={fields.file.title || ""} />
      }

      return null
    },
  }
}

const getInlineEmbedded = () => {
  return {
    [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
      const { internal } = node.data.target

      switch (internal?.type) {
        case "ContentfulButton":
          return mapEmbeddedEntryToButtonLink(node)
        default:
          return null
      }
    },
  }
}

const RichText: AppFunctionComponent<Props> = ({ content, options }) => {
  const richTextOptions: Options = {
    renderMark: getRenderMark(),
    renderNode: {
      ...getHeadings(),
      ...getHyperlinks(),
      ...getEmbedded(),
      ...getInlineEmbedded(),
      ...options,
      // add footnotes
    },
  }

  if (
    content.references?.length > 0 &&
    content.references.some(
      (ref) => ref?.internal?.type === "ContentfulAccordionItem"
    )
  ) {
    return (
      <>
        {content.references.map((ref: AccordionListItemBuilder | any) => {
          if (ref.internal?.type === "ContentfulButton") {
            return (
              <Button key={ref.text} as={BasicButtonLink} to={ref.url}>
                {ref.text}
              </Button>
            )
          }

          const text = ref.accordionText
            ? renderRichText(ref.accordionText, richTextOptions)
            : null
          const slug = kebabCase(ref.title)

          const props = {
            title: ref.title,
            link: typeof window !== "undefined" ? location.pathname : "",
            text,
            slug,
            type: ref.type,
            image: ref.image,
          }

          return (
            <>
              <AccordionItem
                key={props.slug}
                {...props}
                {...interactiveAccordion(props)}
              />
            </>
          )
        })}
      </>
    )
  }

  return <>{renderRichText(content, richTextOptions)}</>
}

export default RichText
